<template>
  <div
    class="list-item"
    :class="{ 'is-sold': isSold }"
    @click="navigateToDetails"
  >
    <!-- Thumbnail -->
    <div class="list-item__thumb">
      <img
        v-if="getImageUrl(machine.images?.[0])"
        :src="getImageUrl(machine.images?.[0])"
        :alt="machine.title"
        @error="handleImageError"
      />
      <div v-else class="list-item__no-img">
        <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v14z"/>
        </svg>
      </div>
      <span v-if="isSold" class="list-item__sold-badge">SOLD</span>
    </div>

    <!-- Content -->
    <div class="list-item__content">
      <!-- Info -->
      <div class="list-item__info">
        <div class="list-item__title">{{ machine.title }}</div>
        <span class="list-item__ref">{{ machine.reference_code }}</span>
        <div class="list-item__specs">
          <div v-if="machine.brand" class="list-item__spec">
            <span class="list-item__spec-label">Brand:</span>
            <span class="list-item__spec-value">{{ machine.brand.name }}</span>
          </div>
          <div v-if="machine.year_manufactured" class="list-item__spec">
            <span class="list-item__spec-label">Year:</span>
            <span class="list-item__spec-value">{{ machine.year_manufactured }}</span>
          </div>
          <div v-if="machine.condition" class="list-item__spec">
            <span class="list-item__spec-label">Condition:</span>
            <span class="list-item__spec-value capitalize">{{ machine.condition }}</span>
          </div>
          <div v-if="machine.category" class="list-item__spec">
            <span class="list-item__spec-label">Type:</span>
            <span class="list-item__spec-value">{{ machine.category.name }}</span>
          </div>
        </div>
      </div>

      <!-- Bottom Row: Availability + Action -->
      <div class="list-item__bottom">
        <div class="list-item__status">
          <span :class="['status-badge', availabilityBadge.class]">{{ availabilityBadge.label }}</span>
        </div>
        <div class="list-item__action" @click.stop>
          <button
            v-if="!isSold"
            @click="emit('inquire', machine)"
            class="btn-quote"
          >
            {{ translations.inquire }}
          </button>
          <span v-else class="sold-text">{{ translations.sold || 'Sold' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { getSafeImageUrl, handleImageError } = useImageHandling();
const { getMachineStatusLabel, getMachineAvailabilityLabel } = useStatusBadge();

const { t } = useI18n();

const props = defineProps({
  machine: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['inquire']);

const isSold = computed(() => props.machine.status === 'sold');

const translations = computed(() => {
  try {
    return {
      reference: t('machines.card.reference'),
      inquire: t('machines.card.inquire'),
      sold: t('machines.card.sold'),
      brand: t('machines.card.brand'),
      year: t('machines.card.year'),
      condition: t('machines.card.condition'),
      category: t('machines.card.category')
    };
  } catch (error) {
    return {
      reference: 'Ref',
      inquire: 'Get Price Quotation',
      sold: 'Sold',
      brand: 'Brand',
      year: 'Year',
      condition: 'Condition',
      category: 'Category'
    };
  }
});

const availabilityBadge = computed(() => ({
  label: getMachineAvailabilityLabel(props.machine.availability, props.machine.available_from),
  class: getAvailabilityClass(props.machine.availability)
}));

const getAvailabilityClass = (availability) => {
  switch (availability) {
    case 'immediately':
      return 'badge-available';
    case 'in_production':
      return 'badge-coming-soon';
    case 'sold':
      return 'badge-sold';
    default:
      return 'badge-available';
  }
};

const getImageUrl = (image) => {
  if (!image) return null;
  // Prefer file_path as it's easier to process; fall back to url
  if (image.file_path) return getSafeImageUrl(image.file_path);
  if (image.url) return getSafeImageUrl(image.url);
  if (image.full_url) return getSafeImageUrl(image.full_url);
  return null;
};

const navigateToDetails = () => {
  navigateTo(`/machines/${props.machine.id}`);
};
</script>

<style scoped>
/* List Item - Large Card with Big Image */
.list-item {
  display: flex;
  align-items: stretch;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s ease;
  min-height: 220px;
}

.list-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.list-item.is-sold {
  opacity: 0.6;
}

/* Thumbnail - Much Larger */
.list-item__thumb {
  position: relative;
  width: 380px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
}

.list-item__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.list-item__no-img {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
}

.list-item__no-img svg {
  width: 48px;
  height: 48px;
}

.list-item__sold-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 6px 12px;
  background: #dc2626;
  color: white;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

/* Content */
.list-item__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  padding: 24px 28px;
  min-width: 0;
}

/* Info */
.list-item__info {
  flex: 1;
  min-width: 0;
}

.list-item__title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.list-item__ref {
  display: inline-block;
  font-size: 12px;
  font-family: 'SF Mono', Monaco, monospace;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 6px;
  margin-bottom: 14px;
}

.list-item__specs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
}

.list-item__spec {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.list-item__spec-label {
  color: #64748b;
  font-weight: 500;
}

.list-item__spec-value {
  color: #0f172a;
  font-weight: 600;
}

/* Bottom Row */
.list-item__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}

/* Status */
.list-item__status {
  flex-shrink: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.status-badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.badge-available {
  background: #ecfdf5;
  color: #059669;
}

.badge-coming-soon {
  background: #dbeafe;
  color: #1e40af;
}

.badge-sold {
  background: #fef2f2;
  color: #dc2626;
}

.badge-reserved {
  background: #fffbeb;
  color: #d97706;
}

.badge-default {
  background: #f8fafc;
  color: #64748b;
}

/* Action */
.list-item__action {
  flex-shrink: 0;
}

.btn-quote {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: #0f172a;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-quote:hover {
  background: #1e293b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.2);
}

.sold-text {
  padding: 14px 28px;
  font-size: 14px;
  font-weight: 600;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 50px;
}

.capitalize {
  text-transform: capitalize;
}

/* Responsive */
@media (max-width: 900px) {
  .list-item__thumb {
    width: 300px;
  }
}

@media (max-width: 768px) {
  .list-item {
    flex-direction: column;
    min-height: auto;
  }
  
  .list-item__thumb {
    width: 100%;
    height: 240px;
  }
  
  .list-item__content {
    padding: 20px;
  }
  
  .list-item__bottom {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  
  .list-item__status {
    order: -1;
    align-self: flex-start;
  }
  
  .btn-quote {
    width: 100%;
    justify-content: center;
    padding: 16px 28px;
  }
}
</style>
